import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../context/ToastContext';
import {
  useArchiveExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpenseQuery,
  useUnarchiveExpenseMutation
} from '../../../../../services/endpoints/finance/expense';
import { Expense } from '../../../../../types/finance.types';
import {
  expensesCategories,
  expensesContext,
  paymentMethods
} from '../../../../../utils/consts/finance/finance';
import { dateToUTC, formatDate } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const ExpenseDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetExpenseQuery(+params.id!);
  const [deleteExpense, { isLoading: isDeleting }] = useDeleteExpenseMutation();
  const [archiveExpense, { isLoading: isArchiving }] = useArchiveExpenseMutation();
  const [unarchiveExpense, { isLoading: isUnarchiving }] = useUnarchiveExpenseMutation();
  const [expense, setExpense] = useState<Expense>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading expense', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setExpense(data.expense);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteExpense(+params.id!).unwrap();
      addToast('Expense deleted successfully', 'success');
      navigate('/admin/finance/expenses');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleArchive = async () => {
    try {
      await archiveExpense(+params.id!).unwrap();
      addToast('Expense archived successfully', 'success');
      setShowArchiveModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleUnarchive = async () => {
    try {
      await unarchiveExpense(+params.id!).unwrap();
      addToast('Expense unarchived successfully', 'success');
      setShowUnarchiveModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !expense) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/finance/expenses')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Expenses List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Expense #${expense?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/finance/expenses/${expense?.id}/edit`)}
          >
            Edit expense
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowArchiveModal(true)}
          >
            Archive Expense
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowUnarchiveModal(true)}
          >
            Unarchive Expense
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Expense
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Category:"
              text={expensesCategories[expense?.category]}
            />
            <LabelText
              label="Context:"
              text={expensesContext[expense?.context]}
            />
            <LabelText label="Admin:">
              <LinkCell
                content={expense?.admin?.full_name}
                url={`/admin/people/admins/${expense?.admin?.id}`}
                underline
              />
            </LabelText>
            <LabelText label="Session:">
              <LinkCell
                content={expense?.course?.title ?? 'No session title'}
                url={`/admin/schedule/sessions/${expense?.course?.id}`}
                underline
              />
            </LabelText>
            <LabelText
              label="Amount:"
              text={`$${expense?.amount.toFixed(2)}`}
            />
            <LabelText
              label="Check Number:"
              text={expense?.check_number ?? 'N/A'}
            />
            <LabelText
              label="Due On:"
              text={expense?.due_on ? dateToUTC(new Date(expense?.due_on), 'MM/dd/yyyy') : 'N/A'}
            />
            <LabelText
              label="Paid On:"
              text={expense?.paid_on ? dateToUTC(new Date(expense?.paid_on), 'MM/dd/yyyy') : 'N/A'}
            />
            <LabelText
              label="Payment Method:"
              text={paymentMethods[expense?.payment_method] ?? 'N/A'}
            />
            <LabelText
              label="Invoice Number:"
              text={expense?.invoice_number ?? 'N/A'}
            />
            <LabelText
              label="Notes:"
              text={expense?.notes ?? 'N/A'}
            />
            <LabelText
              label="Created At:"
              text={formatDate(new Date(expense?.created_at), 'MM/dd/yyyy h:mm a zzz')}
            />
          </DetailSection>
        </Stack>
      </div>

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        isLoading={isDeleting}
      />

      <ConfirmationModal
        show={showArchiveModal}
        title="Archive Expense"
        message="Are you sure you want to archive this expense?"
        confirmText="Yes, Archive"
        cancelText="Cancel"
        onConfirm={handleArchive}
        onCancel={() => setShowArchiveModal(false)}
        isLoading={isArchiving}
      />

      <ConfirmationModal
        show={showUnarchiveModal}
        title="Unarchive Expense"
        message="Are you sure you want to unarchive this expense?"
        confirmText="Yes, Unarchive"
        cancelText="Cancel"
        onConfirm={handleUnarchive}
        onCancel={() => setShowUnarchiveModal(false)}
        isLoading={isUnarchiving}
      />
    </>
  );
};
